import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useAuth } from '../../auth/core/Auth';
import axios from 'axios';
import swal from 'sweetalert2'
import { useNavigate } from 'react-router-dom'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'

const API_URL_REAL = process.env.REACT_APP_API_URL

interface IQuestion {
    id: string;
    text: string;
    textEn: string;
    questionOption: IOption[];
}

interface IOption {
    id: number;
    text: string;
    textEn: string;
    uploadFile: boolean;
}

interface IAnswer {
    questionId: string;
    optionId: number;
}

export const Survey = () => {
    const [question, setQuestion] = useState<IQuestion | null>(null); // only one question at a time
    const [answer, setAnswer] = useState<IAnswer | null>(null); // only one answer at a time
    const [uploadFile, setUploadFile] = useState<File | null>(null); // only one file at a time
    const [selectedOption, setSelectedOption] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const intl = useIntl();
    const lang = useLang()
    const { logout, currentUser, setCurrentUser } = useAuth();
    const navigate = useNavigate();


    useEffect(() => {
        // Fetch the initial question
        axios
            .get(`${API_URL_REAL}/Custom/questions_initial`)
            .then((response) => {
                if (response.data?.status === "DONE" && response.data?.message === "ALREADYCOMPLETED") {
                    swal.fire({
                        title: intl.formatMessage({
                            id: 'Error'
                        }),
                        text: intl.formatMessage({ id: 'You have already completed this.' }),
                        icon: 'error',
                        confirmButtonText: intl.formatMessage({ id: 'Okay' })
                    }).then(async (result) => {  // burada async ekliyoruz
                        await updateSurveyCompleted("1")
                        navigate('/auth');
                    })
                    return;
                }


                if (response.data?.status === "DONE" && response.data?.message === "NEEDATLEASTONERESULT") {
                    swal.fire({
                        title: intl.formatMessage({
                            id: 'Error'
                        }),
                        text: intl.formatMessage({ id: 'You need to have exam result in order to apply. If you made a mistake, please contact student affairs.' }),
                        icon: 'error',
                        confirmButtonText: intl.formatMessage({ id: 'Okay' })


                    }).then((result) => {

                        logout()
                    })
                    return;

                }

                if (response.data?.status === "DONE" && response.data?.message === "CANNOTREGISTER") {
                    swal.fire({
                        title: intl.formatMessage({
                            id: 'Error'
                        }),
                        text: intl.formatMessage({ id: 'You can not register to the Ege University with your current status using EGEYÖS or TR-YÖS. If you think there is a mistake, please contact Student Affairs.' }),
                        icon: 'error',
                        confirmButtonText: intl.formatMessage({ id: 'Okay' })
                    }).then((result) => {
                        navigate('/exam/cannotregister');
                    })
                    return;

                }
                setQuestion(response.data);
            })
            .catch((error) => console.error(error));

    }, []);

    const updateSurveyCompleted = async (newValue: string) => {
        return new Promise<void>((resolve, reject) => {
            if (currentUser?.id === undefined) {
                reject("User ID is undefined.");
                return;
            }

            const updatedUser = {
                ...currentUser,
                surveyCompleted: newValue
            };

            setCurrentUser(updatedUser);
            resolve();
        });
    };

    const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const optionId = e.target.value;
        setSelectedOption(parseInt(optionId));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files ? e.target.files[0] : null;

        if (file) {
            setUploadFile(file);
        }
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        // Check if file upload is required and if a file is uploaded
        if (question && selectedOption) {
            const selectedQuestionOption = question.questionOption.find((option) => option.id === selectedOption);
            if (selectedQuestionOption?.uploadFile && !uploadFile) {
                // Show error message, file upload is required but no file is uploaded
                swal.fire({
                    title: intl.formatMessage({
                        id: 'Error'
                    }),
                    text: intl.formatMessage({ id: 'File upload is required.' }),
                    icon: 'error',
                    confirmButtonText: intl.formatMessage({ id: 'Okay' })
                });
                setIsLoading(false);
                return; // stop further execution
            }

            // Construct the new answer and save it to state
            const newAnswer = { questionId: question.id, optionId: selectedOption };
            setAnswer(newAnswer);

            // Call saveAnswerGetNextQuestion API here
            await saveAnswerGetNextQuestion(newAnswer);
        }
        setIsLoading(false);
    };


    const saveAnswerGetNextQuestion = async (answer: IAnswer) => {  // <-- `async` keyword added
        const data = new FormData();
        data.append('questionId', answer.questionId);
        data.append('optionId', answer.optionId.toString());
        if (uploadFile) {
            data.append('file', uploadFile);
        }

        await axios  // <-- `await` keyword added
            .post(`${API_URL_REAL}/Custom/submit_answer_and_get_next_question`, data)
            .then(async (response) => {
                if (response.data?.status === "DONE") {
                    if (response.data?.message === "CANNOTREGISTER") {
                        swal.fire({
                            title: intl.formatMessage({
                                id: 'Error'
                            }),
                            text: intl.formatMessage({ id: 'You can not register to the Ege University with your current status using EGEYÖS or TR-YÖS. If you think there is a mistake, please contact Student Affairs.' }),
                            icon: 'error',
                            confirmButtonText: intl.formatMessage({ id: 'Okay' })
                        }).then(async (result) => {
                            await updateSurveyCompleted("1")
                            navigate('/exam/cannotregister');
                        })
                        return;

                    }
                    else if (response.data?.message === "NORESTRICTION") {
                        await updateSurveyCompleted("1")
                        navigate('/dashboard');
                    }
                }
                if (response.data) {

                    setQuestion(response.data); // replace the old question with the new one
                    setAnswer(null); // clear out the previous answer
                    setUploadFile(null); // clear out the uploaded file
                } else {
                    // End of the survey, perform desired operations
                }
            })
            .catch((error) => console.error(error));
    };

    return (
        <div className="container">
            {
                currentUser?.fileApproved === "0"
                    ?
                    (
                        <div className='alert alert-danger'>
                            {currentUser.rejectionReason}
                        </div>
                    )
                    :
                    (
                        <div className='alert alert-danger'>
                            {intl.formatMessage({ id: 'Please read the questions carefully and provide your answers attentively. You will not have the opportunity to edit your answers.' })}
                        </div>
                    )
            }
            {question && (
                <div className="mb-5">
                    <p className="mb-5" dangerouslySetInnerHTML={{ __html: lang === "en" ? question.textEn : question.text }} />
                    {question.questionOption.map((option: any) => (
                        <div key={option.id} className="form-check mb-3">
                            <input
                                className="form-check-input"
                                type="radio"
                                id={option.id}
                                name={question.id}
                                value={option.id}
                                onChange={handleOptionChange}
                            />
                            <label className="form-check-label" htmlFor={option.id}>{lang === "en" ? option.textEn : option.text}</label>

                            {selectedOption === option.id && question.questionOption.find((option) => option.id === selectedOption)?.uploadFile && (
                                <>
                                    <div className="alert alert-primary">{lang === "en" ? option.uploadFileDescriptionEn : option.uploadFileDescription}</div>
                                    <div className="mb-5">
                                        <input className="form-control" accept=".pdf" type="file" id="fileUpload" onChange={handleFileChange} required />
                                    </div>
                                </>
                            )}
                        </div>
                    ))}
                    <button disabled={isLoading} className="btn btn-primary mt-3" onClick={handleSubmit}>{intl.formatMessage({ id: 'Next' })}</button>
                </div>
            )}
            <div className="row justify-content-end">
                <div className="col-xl-6 d-flex justify-content-end">
                    <Button variant='danger' onClick={logout}>
                        {intl.formatMessage({ id: 'Logout' })}
                    </Button>
                </div>
            </div>
        </div>
    );
};